// fileActions.js

/**
 * Retry helper for async functions
 */

export const retryUntilSuccess = async (fn, maxAttempts = 5, delayMs = 500) => {
  let attempt = 0;
  while (attempt < maxAttempts) {
    try {
      return await fn();
    } catch (err) {
      attempt++;
      console.warn(`Attempt ${attempt} failed. Retrying in ${delayMs}ms...`, err);
      if (attempt >= maxAttempts) throw err;
      await new Promise(res => setTimeout(res, delayMs));
    }
  }
};

export const sortFiles = (files, key, sortOrder = "asc") => {
  const sorted = [...files].sort((a, b) => {
    const valA = a[key];
    const valB = b[key];

    // If both values are numbers or numeric strings
    if (!isNaN(valA) && !isNaN(valB)) {
      return sortOrder === "asc"
        ? Number(valA) - Number(valB)
        : Number(valB) - Number(valA);
    }

    // Compare as strings, case-insensitive
    const strA = String(valA).toLowerCase();
    const strB = String(valB).toLowerCase();

    if (strA < strB) return sortOrder === "asc" ? -1 : 1;
    if (strA > strB) return sortOrder === "asc" ? 1 : -1;
    return 0;
  });

  const newSortOrder = sortOrder === "asc" ? "desc" : "asc";
  return { sorted, newSortOrder };
};
/**
 * Delete a single file via API
 */
export const deleteFileApi = async (file, tusApi, token) => {
  const response = await fetch(`${tusApi}/api/delete/files/${file.username}/${file.fileId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    if (response.status === 404) {
      console.warn(`File ${file.fileId} not found. Skipping.`);
      return;
    }
    const errData = await response.text();
    throw new Error(`Delete failed: ${response.status} - ${errData}`);
  }
};

/**
 * Delete a single file with retry
 */
export const deleteFile = async (file, tusApi, token) => {
  await retryUntilSuccess(() => deleteFileApi(file, tusApi, token));
};

/**
 * Delete multiple files with retry
 */
export const deleteFiles = async (filesToDelete, tusApi, token, loadFiles, onDeleteSuccess) => {
  const fileIds = filesToDelete.map(f => f.fileId);
  const userId = filesToDelete[0]?.username; // assume same user

  try {
    await fetch(`${tusApi}/api/delete/files/batch`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`
      },
      body: JSON.stringify({ userId, fileIds })
    });
    await loadFiles?.();
    if (typeof onDeleteSuccess === "function") onDeleteSuccess();
  } catch (err) {
    console.error("Failed to delete multiple files:", err);
  }
};

/**
 * Download a single file securely
 */
export const secureDownload = async (username, fileId, fileName, tusApi,token) => {
  const res = await fetch(`${tusApi}/api/downloads/${username}/${fileId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Download failed');

  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = fileName || 'downloaded_file';
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

/**
 * Download a single file with retry
 */
export const handleSecureDownload = async (username, fileId, fileName, tusApi,token) => {
  await retryUntilSuccess(() => secureDownload(username, fileId, fileName, tusApi,token));
};

/**
 * Bulk download files
 */
export const handleBulkDownload = async (selectedFiles, files, tusApi,token) => {
  for (const fileId of selectedFiles) {
    const file = files.find(f => f.fileId === fileId);
    if (file) {
      try {
        await handleSecureDownload(file.username, file.fileId, file.fileName, tusApi,token);
      } catch (err) {
        console.error(`Failed to download ${file.fileName} after retries:`, err);
      }
    }
  }
};

export async function previewFile(fileId, token, tusApi) {
  try {
    // Step 1: request temporary preview token
    const res = await fetch(`${tusApi}/api/uploads/${fileId}/preview-token`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!res.ok) throw new Error("Failed to get preview token");
    const { previewToken } = await res.json();

    // Step 2: open preview with short-lived token
    const previewUrl = `${tusApi}/api/uploads/${fileId}/preview?t=${previewToken}`;
    window.open(previewUrl, "_blank");
  } catch (err) {
    console.error("Error previewing file:", err);
  }
}
